import { useFrame } from "@react-three/fiber";
import { useMemo } from "react";
import * as THREE from "three";
import { useDeviceCapabilities } from "../../hooks/useDeviceCapabilities";
import { smoothedCameraRef } from "./useViewportSync";

// 2D simplex noise embedded in GLSL (Ashima Arts)
const SIMPLEX_NOISE_GLSL = /* glsl */ `
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
	const vec4 C = vec4(0.211324865405187, 0.366025403784439,
		-0.577350269189626, 0.024390243902439);
	vec2 i = floor(v + dot(v, C.yy));
	vec2 x0 = v - i + dot(i, C.xx);
	vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
	vec4 x12 = x0.xyxy + C.xxzz;
	x12.xy -= i1;
	i = mod289(i);
	vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
		+ i.x + vec3(0.0, i1.x, 1.0));
	vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy),
		dot(x12.zw, x12.zw)), 0.0);
	m = m * m;
	m = m * m;
	vec3 x = 2.0 * fract(p * C.www) - 1.0;
	vec3 h = abs(x) - 0.5;
	vec3 ox = floor(x + 0.5);
	vec3 a0 = x - ox;
	m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
	vec3 g;
	g.x = a0.x * x0.x + h.x * x0.y;
	g.yz = a0.yz * x12.xz + h.yz * x12.yw;
	return 130.0 * dot(m, g);
}
`;

// Fullscreen triangle vertex shader — covers viewport with a single triangle
// No geometry needed beyond 3 verts; GPU clips the overshoot for free
const vertexShader = /* glsl */ `
varying vec2 vUv;
void main() {
	// Fullscreen triangle trick: vertices at (-1,-1), (3,-1), (-1,3)
	vUv = uv;
	gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

function buildFragmentShader(octaves: number): string {
  return /* glsl */ `
${SIMPLEX_NOISE_GLSL}

uniform float uTime;
uniform vec2 uCameraPos;
uniform float uZoom;
uniform float uOpacity;

varying vec2 vUv;

void main() {
	// Convert screen UV to world-space coordinates via camera
	vec2 worldPos = (vUv - 0.5) / uZoom + uCameraPos;

	// --- Subtle atmospheric patches (matching the old CSS nebula overlay style) ---
	// Each patch is a separate soft glow at a different world-space position,
	// like the old radial-gradient CSS overlays but camera-aware

	// Patch 1: Deep blue glow
	float p1 = snoise(worldPos * 0.00015 + vec2(500.0, -300.0)) * 0.5 + 0.5;
	p1 = pow(p1, 2.0);
	vec3 patchColor = vec3(0.015, 0.030, 0.090) * p1;

	// Patch 2: Cyan-blue glow (like the old rgba(0, 240, 255, 0.05))
	float p2 = snoise(worldPos * 0.00012 + vec2(-200.0, 400.0)) * 0.5 + 0.5;
	p2 = pow(p2, 2.0);
	patchColor += vec3(0.0, 0.047, 0.050) * p2;

	// Patch 3: Soft blue-pink glow
	float p3 = snoise(worldPos * 0.00018 + vec2(300.0, 100.0)) * 0.5 + 0.5;
	p3 = pow(p3, 2.5);
	patchColor += vec3(0.020, 0.012, 0.035) * p3;

	// Patch 4: Gold glow (like the old rgba(255, 215, 0, 0.03))
	float p4 = snoise(worldPos * 0.00020 + vec2(150.0, -500.0)) * 0.5 + 0.5;
	p4 = pow(p4, 2.5);
	patchColor += vec3(0.030, 0.025, 0.0) * p4;

	// Patch 5: Teal glow (like the old rgba(16, 185, 129, 0.03))
	float p5 = snoise(worldPos * 0.00014 + vec2(-400.0, -150.0)) * 0.5 + 0.5;
	p5 = pow(p5, 2.5);
	patchColor += vec3(0.002, 0.022, 0.015) * p5;

	${
    octaves >= 2
      ? `
	// Subtle cloud texture variation on top of the patches
	float cloud = snoise(worldPos * 0.0005 + uTime * 0.2) * 0.5 + 0.5;
	cloud = pow(cloud, 1.5) * 0.15;
	patchColor *= (0.85 + cloud);
	`
      : ""
  }

	// Very subtle overall brightness variation
	float dimmer = snoise(worldPos * 0.00008 + vec2(-600.0, 200.0)) * 0.5 + 0.5;
	patchColor *= smoothstep(0.2, 0.8, dimmer);

	gl_FragColor = vec4(patchColor, uOpacity);
}
	`;
}

// Fullscreen triangle geometry — 1 triangle, 3 vertices, covers entire screen
function createFullscreenTriGeometry(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  // Positions in clip space: single triangle that covers [-1,1] viewport
  const positions = new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]);
  const uvs = new Float32Array([0, 0, 2, 0, 0, 2]);
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  return geo;
}

export function NebulaBackground(): React.JSX.Element {
  const { isLowPower } = useDeviceCapabilities();

  const octaves = isLowPower ? 1 : 2;
  const baseOpacity = isLowPower ? 0.7 : 1.0;

  const geometry = useMemo(() => createFullscreenTriGeometry(), []);

  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uCameraPos: { value: new THREE.Vector2(0, 0) },
          uZoom: { value: 1 },
          uOpacity: { value: baseOpacity },
        },
        vertexShader,
        fragmentShader: buildFragmentShader(octaves),
        transparent: true,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending,
      }),
    [octaves, baseOpacity],
  );

  useFrame(({ clock }) => {
    const uTime = material.uniforms.uTime;
    const uCameraPos = material.uniforms.uCameraPos;
    const uZoom = material.uniforms.uZoom;
    if (!uTime || !uCameraPos || !uZoom) return;
    uTime.value = clock.getElapsedTime() * 0.00005;
    const cam = smoothedCameraRef.current;
    (uCameraPos.value as THREE.Vector2).set(cam.x * 0.01, cam.y * 0.01);
    uZoom.value = cam.zoom * 0.01;
  });

  return <mesh geometry={geometry} material={material} frustumCulled={false} renderOrder={-10} />;
}
